type PathEntry<T = any> = {
  path: (string | number)[];
  default?: T;
  displayName?: string;
  transform?: (value: any) => T;
};

type PathTree = {
  [key: string]: PathEntry;
};

type ResolvedTree<T extends PathTree> = {
  [K in keyof T]: T[K] extends PathEntry<infer V> ? V : never;
};

type TrieNode = {
  children: Map<string | number, TrieNode>;
  keys: { resultKey: string; default?: any; transform?: (value: any) => any }[];
};

const buildTrie = (tree: PathTree): TrieNode => {
  const root: TrieNode = { children: new Map(), keys: [] };

  const insert = (
    node: TrieNode,
    resultKey: string,
    path: (string | number)[],
    defaultValue?: any,
    transform?: (value: any) => any,
  ) => {
    let current = node;
    for (const segment of path) {
      if (!current.children.has(segment)) {
        current.children.set(segment, { children: new Map(), keys: [] });
      }
      current = current.children.get(segment)!;
    }
    current.keys.push({ resultKey, default: defaultValue, transform });
  };

  for (const [key, entry] of Object.entries(tree)) {
    insert(root, key, entry.path, entry.default, entry.transform);
  }

  return root;
};

const traverse = (obj: any, node: TrieNode, result: Record<string, any>) => {
  for (const { resultKey, default: def, transform } of node.keys) {
    const raw = obj !== undefined ? obj : def;
    result[resultKey] = transform ? transform(raw) : raw;
  }

  for (const [segment, child] of node.children) {
    traverse(obj?.[segment], child, result);
  }
};

const getValues = <T extends PathTree>(
  obj: Record<string, any>,
  tree: TrieNode
): ResolvedTree<T> => {
  const result: Record<string, any> = {};
  traverse(obj, tree, result);
  return result as ResolvedTree<T>;
};

export { getValues, buildTrie };
export type { PathTree, ResolvedTree, PathEntry };